<template>
  <CognitoBrandedShell>
    <!-- Invalid / error -->
    <div
      v-if="errorDetail"
      class="w-full max-w-md space-y-6 rounded-2xl border border-gray-200 bg-white p-8 text-left shadow-lg sm:text-center"
    >
      <div class="space-y-3">
        <p
          v-if="errorHeading"
          class="text-lg font-semibold leading-snug text-red-600"
        >
          {{ errorHeading }}
        </p>
        <p class="whitespace-pre-line text-base leading-relaxed text-gray-600">
          {{ errorDetail }}
        </p>
      </div>
      <p class="text-sm text-gray-500">
        Returning to the home screen in {{ redirectSecondsRemaining }} seconds…
      </p>
      <button
        type="button"
        class="w-full rounded-lg bg-[#9489F5] px-4 py-3 text-sm font-medium text-white shadow-sm hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[#9489F5] focus:ring-offset-2 focus:ring-offset-white"
        @click="returnToHome"
      >
        Return to home
      </button>
    </div>

    <!-- Valid invite: email gate -->
    <div
      v-else-if="invitedEmailPreview"
      class="flex w-full max-w-md flex-col items-center"
    >
      <div
        v-if="showInviteCompanyLogo"
        class="mb-6 flex h-20 max-h-[80px] w-full shrink-0 items-center justify-center px-2"
      >
        <img
          :src="inviteCompanyLogoUrl || undefined"
          alt=""
          class="max-h-[80px] max-w-full object-contain"
          @error="onInviteCompanyLogoError"
        />
      </div>
      <div
        class="w-full rounded-2xl border border-gray-200 bg-white p-8 shadow-lg"
      >
        <h1 class="text-xl font-semibold text-gray-900">
          Accept your invitation
        </h1>
        <p class="mt-3 text-sm leading-relaxed text-gray-600">
          <template v-if="inviteRedirectMode === 'signup'">
            You've been invited to join. To accept, continue to sign up using
            the email address below.
          </template>
          <template v-else>
            You've been invited. Sign in using the email address below.
          </template>
        </p>
        <p
          class="mt-4 break-all rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 font-mono text-sm text-gray-900"
        >
          {{ invitedEmailPreview }}
        </p>
        <div
          class="mt-6 flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-end"
        >
          <button
            type="button"
            class="order-2 inline-flex h-[50px] min-w-[30%] items-center justify-center rounded-md border border-gray-300 bg-white px-3 text-base font-medium leading-6 text-gray-700 antialiased hover:bg-gray-50 sm:order-1"
            @click="copyInvitedEmail"
          >
            {{ copyEmailLabel }}
          </button>
          <button
            type="button"
            class="order-1 inline-flex h-[50px] min-w-[30%] items-center justify-center rounded-md px-3 text-base font-medium leading-6 antialiased focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white sm:order-2"
            :class="
              hasCopiedEmail
                ? 'cursor-pointer border border-gray-200 bg-[rgb(124,58,237)] text-white hover:bg-[rgb(109,40,217)] focus:ring-[rgb(124,58,237)]'
                : 'cursor-not-allowed border border-gray-200 bg-[rgb(124,58,237)]/12 text-[rgb(124,58,237)]/50'
            "
            :disabled="!hasCopiedEmail"
            @click="continueToHostedAuth"
          >
            {{ inviteRedirectMode === "signin" ? "Sign in" : "Accept invite" }}
          </button>
        </div>
      </div>
    </div>

    <!-- Checking invitation (minimum display time + API) -->
    <BarLoader
      v-else
      variant="spinner"
      theme="light"
      contained
      message="Checking your invitation…"
    />
  </CognitoBrandedShell>
</template>

<script lang="ts">
import { defineComponent, computed, onMounted, onUnmounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import axios from "axios";
import {
  cognitoSignIn,
  cognitoSignUpRedirect,
  clearCognitoSessionLocalOnly,
} from "@/cognito/auth";
import { useCognitoAuth } from "@/cognito/cognito.config";
import {
  PENDING_SUBSCRIPTION_INVITE_SLUG_KEY,
  PENDING_COMPANY_EMAIL_INVITE_TOKEN_KEY,
} from "@/constants/subscriptionInvite";
import { railsApiOrigin } from "@/utils/railsApiOrigin";
import BarLoader from "@/components/shared/BarLoader.vue";
import CognitoBrandedShell from "@/components/cognito/CognitoBrandedShell.vue";
import {
  inviteInvalidFriendlyError,
  friendlySignInFailureDisplay,
} from "@/cognito/cognitoCallbackErrors";

const INVITE_INVALID_MESSAGE = "No valid invitation found.";

function userEmailFromValidateBody(data: unknown): string | null {
  if (typeof data !== "object" || data === null) return null;
  const raw = (data as { user_email?: unknown }).user_email;
  if (typeof raw !== "string") return null;
  const t = raw.trim();
  return t || null;
}

function existingUserFromValidateBody(data: unknown): boolean {
  if (typeof data !== "object" || data === null) return false;
  return (data as { existing_user?: unknown }).existing_user === true;
}

function companyLogoUrlFromValidateBody(data: unknown): string | null {
  if (typeof data !== "object" || data === null) return null;
  const raw = (data as { company_logo_url?: unknown }).company_logo_url;
  if (typeof raw !== "string") return null;
  const t = raw.trim();
  return t || null;
}

export default defineComponent({
  name: "CompanyEmailInviteCognitoLandingView",
  components: { BarLoader, CognitoBrandedShell },

  setup() {
    const route = useRoute();
    const router = useRouter();
    const errorHeading = ref("");
    const errorDetail = ref("");
    const invitedEmailPreview = ref<string | null>(null);
    const inviteCompanyLogoUrl = ref<string | null>(null);
    const inviteCompanyLogoFailed = ref(false);
    const showInviteCompanyLogo = computed(
      () =>
        Boolean(inviteCompanyLogoUrl.value) && !inviteCompanyLogoFailed.value
    );
    const inviteTokenForRedirect = ref<string | null>(null);
    const inviteRedirectMode = ref<"signin" | "signup">("signup");
    const copyEmailLabel = ref("Copy email");
    /** Primary action stays muted until copy succeeds (Hosted UI may not prefill email). */
    const hasCopiedEmail = ref(false);
    const validating = ref(true);
    const redirectSecondsRemaining = ref(10);
    let redirectIntervalId: ReturnType<typeof setInterval> | null = null;
    let isNavigatingHome = false;

    function clearRedirectCountdown(): void {
      if (redirectIntervalId !== null) {
        clearInterval(redirectIntervalId);
        redirectIntervalId = null;
      }
    }

    function startRedirectCountdown(): void {
      clearRedirectCountdown();
      redirectSecondsRemaining.value = 10;
      redirectIntervalId = window.setInterval(() => {
        if (redirectSecondsRemaining.value <= 1) {
          clearRedirectCountdown();
          void returnToHome();
          return;
        }
        redirectSecondsRemaining.value -= 1;
      }, 1000);
    }

    async function returnToHome(): Promise<void> {
      if (isNavigatingHome) return;
      isNavigatingHome = true;
      clearRedirectCountdown();
      await clearCognitoSessionLocalOnly();
      await router.push({ path: "/" });
    }

    function setInviteInvalidDisplay(): void {
      const d = inviteInvalidFriendlyError();
      errorHeading.value = d.heading;
      errorDetail.value = d.detail;
      startRedirectCountdown();
    }

    function setGenericErrorDisplay(err: unknown): void {
      const d = friendlySignInFailureDisplay(err);
      errorHeading.value = d.heading;
      errorDetail.value = d.detail;
      startRedirectCountdown();
    }

    function setSimpleError(heading: string, detail: string): void {
      errorHeading.value = heading;
      errorDetail.value = detail;
      startRedirectCountdown();
    }

    async function copyInvitedEmail(): Promise<void> {
      const text = invitedEmailPreview.value;
      if (!text) return;
      try {
        await navigator.clipboard.writeText(text);
        hasCopiedEmail.value = true;
        copyEmailLabel.value = "Copied";
        window.setTimeout(() => {
          copyEmailLabel.value = "Copy email";
        }, 2000);
      } catch {
        copyEmailLabel.value = "Copy failed";
        window.setTimeout(() => {
          copyEmailLabel.value = "Copy email";
        }, 2000);
      }
    }

    async function startHostedAuth(
      token: string,
      mode: "signin" | "signup",
      loginHint?: string
    ): Promise<void> {
      sessionStorage.removeItem(PENDING_SUBSCRIPTION_INVITE_SLUG_KEY);
      sessionStorage.setItem(PENDING_COMPANY_EMAIL_INVITE_TOKEN_KEY, token);
      if (mode === "signin") {
        await cognitoSignIn();
        return;
      }
      const hint = loginHint?.trim();
      await cognitoSignUpRedirect(hint ? { loginHint: hint } : undefined);
    }

    async function continueToHostedAuth(): Promise<void> {
      if (!hasCopiedEmail.value) return;
      const token = inviteTokenForRedirect.value;
      if (!token) return;
      try {
        await startHostedAuth(
          token,
          inviteRedirectMode.value,
          invitedEmailPreview.value ?? undefined
        );
      } catch (e) {
        sessionStorage.removeItem(PENDING_COMPANY_EMAIL_INVITE_TOKEN_KEY);
        invitedEmailPreview.value = null;
        inviteCompanyLogoUrl.value = null;
        inviteCompanyLogoFailed.value = false;
        inviteTokenForRedirect.value = null;
        inviteRedirectMode.value = "signup";
        hasCopiedEmail.value = false;
        validating.value = false;
        setGenericErrorDisplay(
          e instanceof Error ? e : new Error("Could not start authentication.")
        );
      }
    }

    async function proceedWithoutEmailGate(
      token: string,
      mode: "signin" | "signup"
    ): Promise<void> {
      try {
        await startHostedAuth(token, mode);
      } catch (e) {
        sessionStorage.removeItem(PENDING_COMPANY_EMAIL_INVITE_TOKEN_KEY);
        validating.value = false;
        setGenericErrorDisplay(
          e instanceof Error ? e : new Error("Could not start authentication.")
        );
      }
    }

    onMounted(async () => {
      validating.value = true;
      if (!useCognitoAuth) {
        setSimpleError(
          "Sign-in isn’t available here",
          "This invitation link requires Cognito sign-up. Contact your company administrator or support if this message appears again."
        );
        validating.value = false;
        return;
      }

      const token = (route.params.token as string)?.trim();
      if (!token) {
        setSimpleError(
          "Link incomplete",
          "This address is missing the invitation token. Open the company invitation from your email and use the link from that message."
        );
        validating.value = false;
        return;
      }

      const origin = railsApiOrigin();
      if (!origin) {
        setSimpleError(
          "Something’s wrong with the app setup",
          "The app can’t reach the server. Try again later or contact support."
        );
        validating.value = false;
        return;
      }

      try {
        const res = await axios.get(
          `${origin}/company/email_invitations/validate`,
          {
            params: { token },
            validateStatus: (s) => s === 200 || s === 422,
          }
        );

        if (res.status !== 200) {
          const isKnownInvalid =
            typeof res.data === "object" &&
            res.data !== null &&
            "errors" in res.data &&
            (res.data as { errors?: string }).errors === INVITE_INVALID_MESSAGE;
          if (isKnownInvalid) {
            setInviteInvalidDisplay();
          } else {
            setSimpleError(
              "Invitation not available",
              "This invitation is not valid or has already been used. Ask your team for a new invite if you still need access."
            );
          }
          validating.value = false;
          return;
        }

        const hinted = userEmailFromValidateBody(res.data);
        const existingUser = existingUserFromValidateBody(res.data);
        const logoUrl = companyLogoUrlFromValidateBody(res.data);
        if (hinted) {
          invitedEmailPreview.value = hinted;
          inviteCompanyLogoUrl.value = logoUrl;
          inviteCompanyLogoFailed.value = false;
          inviteTokenForRedirect.value = token;
          inviteRedirectMode.value = existingUser ? "signin" : "signup";
          hasCopiedEmail.value = false;
          validating.value = false;
          return;
        }

        await proceedWithoutEmailGate(
          token,
          existingUser ? "signin" : "signup"
        );
      } catch (e) {
        setGenericErrorDisplay(
          e instanceof Error
            ? e
            : new Error(
                "Could not verify this invitation. Check your connection and try again."
              )
        );
        validating.value = false;
      }
    });

    onUnmounted(() => {
      clearRedirectCountdown();
    });

    function onInviteCompanyLogoError(): void {
      inviteCompanyLogoFailed.value = true;
    }

    return {
      errorHeading,
      errorDetail,
      invitedEmailPreview,
      inviteCompanyLogoUrl,
      showInviteCompanyLogo,
      onInviteCompanyLogoError,
      inviteRedirectMode,
      copyEmailLabel,
      hasCopiedEmail,
      validating,
      redirectSecondsRemaining,
      returnToHome,
      copyInvitedEmail,
      continueToHostedAuth,
    };
  },
});
</script>
